import React, { useState, useEffect } from "react";
import { Calendar, BookOpen, Play, Clock, AlertCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";

const WP_BASE = "https://dimensionias.com/cms";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
const stripHtml = (html = "") => {
  try {
    const doc = new DOMParser().parseFromString(html, "text/html");
    return doc.body.textContent || "";
  } catch {
    return html.replace(/<[^>]*>/g, "");
  }
};

const truncate = (text = "", words = 12) => {
  const parts = text.trim().split(/\s+/);
  return parts.length <= words ? text : parts.slice(0, words).join(" ") + "…";
};

const formatDate = (dateStr) => {
  try {
    return new Date(dateStr).toLocaleDateString("en-IN", {
      day: "numeric", month: "short", year: "numeric"
    });
  } catch {
    return dateStr;
  }
};

const readMinutes = (html = "") => {
  const words = stripHtml(html).split(/\s+/).length;
  return `${Math.max(1, Math.ceil(words / 200))} min read`;
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
const InfoGridSection = ({ onNavigateToBlog, onOpenPost }) => {
  const navigate = useNavigate();

  // --- Blog posts state ---
  const [blogs, setBlogs]           = useState([]);
  const [blogsLoading, setBlogsLoading]   = useState(true);
  const [blogsError, setBlogsError]     = useState(false);

  // --- Current Affairs state ---
  const [currentAffairs, setCurrentAffairs]       = useState([]);
  const [caLoading, setCaLoading]     = useState(true);
  const [caError, setCaError]         = useState(false);

  // ---------------------------------------------------------------------------
  // Fetch latest 3 blog posts (category slug: "blogs")
  // ---------------------------------------------------------------------------
  useEffect(() => {
    const fetchBlogs = async () => {
      setBlogsLoading(true);
      setBlogsError(false);
      try {
        // First resolve category id for "blogs" slug
        const catRes = await fetch(
          `${WP_BASE}/wp-json/wp/v2/categories?slug=blogs&per_page=5`
        );
        let catParam = "";
        if (catRes.ok) {
          const cats = await catRes.json();
          if (cats.length > 0) catParam = `&categories=${cats[0].id}`;
        }

        const res = await fetch(
          `${WP_BASE}/wp-json/wp/v2/posts?per_page=3&_embed=true&orderby=date&order=desc${catParam}`
        );
        if (!res.ok) throw new Error("Failed");
        const data = await res.json();

        setBlogs(
          data.map((post) => ({
            id:       post.id,
            title:    post.title?.rendered ? stripHtml(post.title.rendered) : "Untitled",
            subtitle: truncate(stripHtml(post.excerpt?.rendered || post.content?.rendered), 12),
            readTime: readMinutes(post.content?.rendered),
            author:   post._embedded?.author?.[0]?.name || "DimensionIAS",
            date:     formatDate(post.date),
            link:     post.link,
            slug:     post.slug
          }))
        );
      } catch {
        setBlogsError(true);
      } finally {
        setBlogsLoading(false);
      }
    };

    fetchBlogs();
  }, []);

  // ---------------------------------------------------------------------------
  // Fetch latest 3 current affairs posts (category slug: "current-affairs")
  // ---------------------------------------------------------------------------
  useEffect(() => {
    const fetchCA = async () => {
      setCaLoading(true);
      setCaError(false);
      try {
        const catRes = await fetch(
          `${WP_BASE}/wp-json/wp/v2/categories?slug=current-affairs&per_page=5`
        );
        let catParam = "";
        if (catRes.ok) {
          const cats = await catRes.json();
          if (cats.length > 0) catParam = `&categories=${cats[0].id}`;
        }

        const res = await fetch(
          `${WP_BASE}/wp-json/wp/v2/posts?per_page=3&_embed=true&orderby=date&order=desc${catParam}`
        );
        if (!res.ok) throw new Error("Failed");
        const data = await res.json();

        setCurrentAffairs(
          data.map((post) => ({
            id:       post.id,
            title:    stripHtml(post.title?.rendered || ""),
            subtitle: truncate(stripHtml(post.excerpt?.rendered || post.content?.rendered), 12),
            date:     formatDate(post.date),
            slug:     post.slug
          }))
        );
      } catch {
        setCaError(true);
      } finally {
        setCaLoading(false);
      }
    };

    fetchCA();
  }, []);

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------
  const handleBlogClick = (blog) => {
    if (onOpenPost)       { onOpenPost(blog.id);    return; }
    if (onNavigateToBlog) { onNavigateToBlog(blog.id); return; }
    navigate("/blogs");
  };

  const handleCAClick = () => navigate("/current-affairs");

  const handleViewAllBlogs = () => {
    if (onNavigateToBlog) { onNavigateToBlog(); return; }
    navigate("/blogs");
  };

  // ---------------------------------------------------------------------------
  // Render helpers
  // ---------------------------------------------------------------------------
  const LoadingRows = ({ count = 3, color = "purple" }) => (
    <ul className="space-y-3">
      {Array.from({ length: count }).map((_, i) => (
        <li key={i} className={`p-3 bg-${color}-50 rounded-lg border-l-4 border-${color}-300 animate-pulse`}>
          <div className="h-3 bg-gray-200 rounded w-3/4 mb-2" />
          <div className="h-2 bg-gray-100 rounded w-1/2" />
        </li>
      ))}
    </ul>
  );

  const ErrorBanner = ({ color = "purple", label = "content" }) => (
    <div className={`flex items-center gap-2 p-3 bg-${color}-50 rounded-lg text-${color}-700 text-sm border border-${color}-200`}>
      <AlertCircle size={16} />
      <span>Could not load {label}. Check back later.</span>
    </div>
  );

  // ---------------------------------------------------------------------------
  // JSX
  // ---------------------------------------------------------------------------
  return (
    <section className="py-5 sm:py-6 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">

          {/* ---- Column 1: Current Affairs ---- */}
          <div className="bg-white rounded-2xl p-4 shadow-sm border hover:shadow-md transition-shadow duration-300">
            <h2 className="text-base font-bold text-gray-800 mb-3 text-center flex items-center justify-center gap-2">
              <Calendar className="text-blue-600" size={18} />
              Current Affairs
            </h2>

            {caLoading && <LoadingRows count={3} color="blue" />}
            {!caLoading && caError && <ErrorBanner color="blue" label="current affairs" />}
            {!caLoading && !caError && currentAffairs.length === 0 && (
              <p className="text-xs text-gray-400 text-center py-2">No posts found.</p>
            )}
            {!caLoading && !caError && currentAffairs.length > 0 && (
              <ul className="space-y-2">
                {currentAffairs.map((item) => (
                  <li
                    key={item.id}
                    onClick={handleCAClick}
                    className="flex items-start gap-2 p-2 bg-blue-50/80 rounded-lg hover:bg-blue-100 transition-colors duration-150 cursor-pointer border-l-4 border-blue-500"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 mb-0.5">
                        <span className="font-bold text-gray-800 text-xs line-clamp-1">{item.title}</span>
                        <span className="shrink-0 bg-blue-600 text-white text-[9px] px-1.5 py-0.2 rounded-full font-bold">NEW</span>
                      </div>
                      <p className="text-gray-500 text-[10px] flex items-center gap-1">
                        <Clock size={9} /> {item.date}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            )}

            <div className="mt-3 text-center">
              <button
                onClick={handleCAClick}
                className="bg-gradient-to-r from-blue-500 to-cyan-500 text-white px-4 py-1.5 rounded-full text-xs font-bold hover:shadow transition-all duration-200"
              >
                View All →
              </button>
            </div>
          </div>

          {/* ---- Column 2: Blogs & Recent Activities ---- */}
          <div className="bg-white rounded-2xl p-4 shadow-sm border hover:shadow-md transition-shadow duration-300">
            <h2 className="text-base font-bold text-gray-800 mb-3 text-center flex items-center justify-center gap-2">
              <BookOpen className="text-purple-600" size={18} />
              Blogs &amp; Recent Activities
            </h2>

            {blogsLoading && <LoadingRows count={3} color="purple" />}
            {!blogsLoading && blogsError && <ErrorBanner color="purple" label="blogs" />}
            {!blogsLoading && !blogsError && blogs.length === 0 && (
              <p className="text-xs text-gray-400 text-center py-2">No posts found.</p>
            )}
            {!blogsLoading && !blogsError && blogs.length > 0 && (
              <ul className="space-y-2">
                {blogs.map((item) => (
                  <li
                    key={item.id}
                    onClick={() => handleBlogClick(item)}
                    className="p-2 bg-purple-50/80 rounded-lg hover:bg-purple-100 transition-colors duration-150 cursor-pointer border-l-4 border-purple-500"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-gray-800 text-xs line-clamp-1 mb-0.5">{item.title}</p>
                      <div className="flex items-center gap-2 text-[10px] text-gray-500">
                        <span className="flex items-center gap-1"><Clock size={9} />{item.readTime}</span>
                        <span>by {item.author}</span>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}

            <div className="mt-3 text-center">
              <button
                onClick={handleViewAllBlogs}
                className="bg-gradient-to-r from-purple-600 to-blue-600 text-white px-4 py-1.5 rounded-full text-xs font-bold hover:shadow transition-all duration-200"
              >
                View All Blogs →
              </button>
            </div>
          </div>

          {/* ---- Column 3: UPSC / MPSC Resources (YouTube) ---- */}
          <div className="bg-white rounded-2xl p-4 shadow-sm border hover:shadow-md transition-shadow duration-300 flex flex-col justify-between">
            <h2 className="text-base font-bold text-gray-800 mb-2 text-center flex items-center justify-center gap-2">
              <Play className="text-green-600" size={18} />
              UPSC / MPSC Resources
            </h2>

            <div className="grid grid-cols-2 md:grid-cols-1 gap-2 flex-1 items-center">
              <iframe
                className="w-full h-32 rounded-lg shadow-xs"
                src="https://www.youtube.com/embed/R-LGKfxDRA0"
                title="DimensionIAS YouTube Video 1"
                allowFullScreen
              />
              <iframe
                className="w-full h-32 rounded-lg shadow-xs"
                src="https://www.youtube.com/embed/tY1ycJk-B0g"
                title="DimensionIAS YouTube Video 2"
                allowFullScreen
              />
            </div>
          </div>

        </div>
      </div>
    </section>
  );
};

export default InfoGridSection;
